import type { CalloutNotesSectionNote } from '@/components/CalloutNotesSection'
import {
  deriveLockEpochs,
  lastRevisedAt,
  wasLockedAt,
} from '@/db/domain/lockEpochs'
import type { Note, StatusChangeLog } from '@/lib/schemas/db'

/**
 * The Work Notes feed: notes and status changes merged into one timeline.
 *
 * Callouts, sessions and jobs all render the same feed, so this is the single
 * place that decides what a reader sees — including which status changes are
 * promoted to lock markers and which notes are flagged as having been changed
 * while the record was closed.
 */
export function buildServiceFeed(
  logs: Note[] | undefined,
  statusChanges: StatusChangeLog[] | undefined,
  getAuthorDisplayName: (authorId: string) => string,
): CalloutNotesSectionNote[] {
  const epochs = deriveLockEpochs(statusChanges)
  const lockOpenerIds = new Set(epochs.map((epoch) => epoch.statusChangeId))
  const reopenTimes = new Set(
    epochs
      .map((epoch) => epoch.reopenedAt)
      .filter((at): at is string => Boolean(at)),
  )

  const noteItems: CalloutNotesSectionNote[] = (logs ?? []).map((log) =>
    toFeedNote(log, getAuthorDisplayName, epochs),
  )

  const statusItems: CalloutNotesSectionNote[] = (statusChanges ?? []).map(
    (change) => {
      const common = {
        id: change.id,
        authorName: getAuthorDisplayName(change.authorId),
        createdAt: change.createdAt,
        occurredAt: change.occurredAt,
        previousStatus: change.previousStatus,
        newStatus: change.newStatus,
        reason: change.reason,
      }

      const opensLock = lockOpenerIds.has(change.id)
      const isReopen = !opensLock && reopenTimes.has(change.createdAt)

      // One artifact per event: a lock marker carries everything the routine
      // status row would, so it replaces that row instead of joining it.
      return opensLock || isReopen
        ? { ...common, kind: 'lock' as const, isReopen }
        : { ...common, kind: 'status_change' as const }
    },
  )

  return [...noteItems, ...statusItems].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  )
}

/**
 * A customer-note thread (general notes, or one follow-up's answers), rendered
 * by the same feed component as service work notes.
 *
 * There are no status changes and no locks here — a customer is never closed —
 * so this is the note timeline alone, with the lock machinery given nothing to
 * find.
 */
export function buildCustomerNoteFeed(
  notes: FeedNote[] | undefined,
  getAuthorDisplayName: (authorId: string) => string,
): CalloutNotesSectionNote[] {
  const noLocks = deriveLockEpochs(undefined)

  return (notes ?? [])
    .map((note) => toFeedNote(note, getAuthorDisplayName, noLocks))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

/**
 * What the feed reads off a note. Service notes and customer notes differ only
 * in what they hang off, which the feed never looks at.
 */
type FeedNote = Omit<Note, 'serviceId'>

function toFeedNote(
  log: FeedNote,
  getAuthorDisplayName: (authorId: string) => string,
  epochs: ReturnType<typeof deriveLockEpochs>,
): CalloutNotesSectionNote {
  const audit = {
    revisions: log.revisions?.map((revision) => ({
      text: revision.text,
      caption: revision.caption,
      photoUrl: revision.photoUrl,
      photoUrls: revision.photoUrls,
      at: revision.at,
      authorName: getAuthorDisplayName(revision.authorId),
    })),
    deletedAt: log.deletedAt,
    deletedByName: log.deletedBy
      ? getAuthorDisplayName(log.deletedBy)
      : undefined,
    // Where a note sits in the feed already shows when it was written, so
    // only a change made after the record closed is worth marking.
    editedWhileLocked: wasLockedAt(epochs, lastRevisedAt(log.revisions)),
    deletedWhileLocked: wasLockedAt(epochs, log.deletedAt),
  }

  return log.photoUrl
    ? {
        id: log.id,
        kind: 'photo' as const,
        authorName: getAuthorDisplayName(log.authorId),
        createdAt: log.createdAt,
        photoUrl: log.photoUrl ?? '',
        photoUrls: log.photoUrls,
        caption: log.caption ?? '',
        ...audit,
      }
    : {
        id: log.id,
        kind: 'text' as const,
        authorName: getAuthorDisplayName(log.authorId),
        createdAt: log.createdAt,
        text: log.text ?? '',
        ...audit,
      }
}
